import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Heading from "../components/common/Heading";
import { useAuth, signedFetch } from "../components/context/AuthContext";
import { useBookings } from "../components/context/BookingContext";

const getTodayString = () => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export default function Booking() {
  const { user } = useAuth();
  const { rooms, validateCoupon } = useBookings();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    number: "",
    checkInDate: "",
    checkInTime: "12:00",
    checkOutDate: "",
    checkOutTime: "11:00",
    adults: "adult 1",
    children: "Child 0",
    roomType: "Standard Room",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [step, setStep] = useState(1);

  // Coupon state variables
  const [couponCodeInput, setCouponCodeInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

  const getRoomPrice = () => {
    const room = rooms.find((r) => r.roomType === formData.roomType);
    if (room) return parseFloat(room.pricePerNight);
    if (formData.roomType === "Deluxe Room") return 5999;
    if (formData.roomType === "Super Deluxe") return 8999;
    return 3999; // Standard Room fallback
  };

  const calculateNights = () => {
    if (!formData.checkInDate || !formData.checkOutDate) return 1;
    const checkInVal = new Date(formData.checkInDate);
    const checkOutVal = new Date(formData.checkOutDate);
    const diffTime = Math.abs(checkOutVal - checkInVal);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) || 1;
    return diffDays;
  };

  const handleApplyCoupon = async () => {
    if (!couponCodeInput) return;
    setCouponError("");
    setIsValidatingCoupon(true);

    const pricePerNight = getRoomPrice();
    const nights = calculateNights();
    const subtotal = pricePerNight * nights;

    const res = await validateCoupon(couponCodeInput.toUpperCase().trim(), subtotal);
    setIsValidatingCoupon(false);
    
    if (res.success) {
      setAppliedCoupon({
        code: res.coupon.code,
        discountAmount: res.coupon.discountAmount,
        discountValue: res.coupon.discountValue,
        discountType: res.coupon.discountType,
        minBookingAmount: res.coupon.minBookingAmount,
        finalCost: res.coupon.finalCost
      });
      setCouponError("");
    } else {
      setAppliedCoupon(null);
      setCouponError(res.error || "Invalid coupon code.");
    }
  };

  // Clear coupon if room type or dates change
  useEffect(() => {
    setAppliedCoupon(null);
    setCouponCodeInput("");
    setCouponError("");
  }, [formData.roomType, formData.checkInDate, formData.checkOutDate]);

  // Pre-fill user data if authenticated
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        fullName: user.fullName || "",
        email: user.email || "",
        number: user.number || "",
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setErrors((prev) => ({
      ...prev,
      [e.target.name]: "",
    }));
  };

  const validateStep = (currentStep) => {
    const newErrors = {};

    if (currentStep === 1) {
      if (!formData.fullName) newErrors.fullName = "Full Name is required.";
      if (!formData.email) newErrors.email = "Email is required.";
      if (!formData.number) newErrors.number = "Phone number is required.";
    }

    if (currentStep === 2) {
      const todayStr = getTodayString();
      if (!formData.checkInDate) {
        newErrors.checkInDate = "Check-in date is required.";
      } else if (formData.checkInDate < todayStr) {
        newErrors.checkInDate = "Check-in date cannot be in the past.";
      }

      if (!formData.checkInTime) {
        newErrors.checkInTime = "Check-in time is required.";
      }

      if (!formData.checkOutDate) {
        newErrors.checkOutDate = "Check-out date is required.";
      } else if (formData.checkInDate && formData.checkOutDate < formData.checkInDate) {
        newErrors.checkOutDate = "Check-out date cannot be before check-in date.";
      }

      if (!formData.checkOutTime) {
        newErrors.checkOutTime = "Check-out time is required.";
      }

      if (formData.checkInDate && formData.checkInTime && formData.checkOutDate && formData.checkOutTime) {
        const checkInDateTime = new Date(`${formData.checkInDate}T${formData.checkInTime}`);
        const checkOutDateTime = new Date(`${formData.checkOutDate}T${formData.checkOutTime}`);
        const minTimeDiff = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
        
        if (isNaN(checkInDateTime.getTime()) || isNaN(checkOutDateTime.getTime())) {
          newErrors.checkOutDate = "Invalid stay date or time selected.";
        } else if (checkOutDateTime.getTime() - checkInDateTime.getTime() < minTimeDiff) {
          newErrors.checkOutDate = "Check-out must be at least 24 hours after check-in.";
        }
      }
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const validateAll = () => {
    const steps = [1, 2];
    for (const s of steps) {
      const ok = validateStep(s);
      if (!ok) {
        setStep(s);
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep((prev) => Math.min(prev + 1, 3));
    }
  };

  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateAll()) return;

    setIsSubmitting(true);

    // Save to client local bookings database (backend fallback)
    try {
      const existingBookings = JSON.parse(localStorage.getItem("bookings") || "[]");
      const newBooking = {
        id: Date.now().toString(),
        fullName: formData.fullName,
        email: formData.email,
        number: formData.number,
        checkInDate: formData.checkInDate,
        checkInTime: formData.checkInTime,
        checkOutDate: formData.checkOutDate,
        checkOutTime: formData.checkOutTime,
        adults: formData.adults,
        children: formData.children,
        roomType: formData.roomType,
        timestamp: new Date().toISOString(),
        status: "Pending Check-In"
      };
      existingBookings.push(newBooking);
      localStorage.setItem("bookings", JSON.stringify(existingBookings));
    } catch (err) {
      console.warn("localStorage bookings save failed", err);
    }

    const pricePerNight = getRoomPrice();
    const nights = calculateNights();
    const subtotal = pricePerNight * nights;

    // Sync booking to Google Sheets via secure API Gateway
    signedFetch({
      action: "createBooking",
      fullName: formData.fullName,
      email: formData.email,
      number: formData.number,
      roomType: formData.roomType,
      checkInDate: formData.checkInDate,
      checkInTime: formData.checkInTime,
      checkOutDate: formData.checkOutDate,
      checkOutTime: formData.checkOutTime,
      adults: formData.adults,
      children: formData.children,
      totalCost: subtotal,
      couponCode: appliedCoupon ? appliedCoupon.code : "",
    })
      .then((data) => {
        setIsSubmitting(false);
        if (data && data.success) {
          setIsSubmitted(true);
        } else {
          setSubmitError(data?.error || "Booking submission failed. Please check your reservation details and try again.");
        }
      })
      .catch((error) => {
        setIsSubmitting(false);
        console.error("API Error:", error);
        setSubmitError("Network connection error. Please verify your internet connection and try again.");
      });
  };

  return (
    <>
      <Helmet>
        <title>Online Room Booking & Reservations | HariKunj Resort Jodhpur</title>
        <meta
          name="description"
          content="Book your royal rooms and suites online at HariKunj Resort, Jodhpur. Safe, secure, and intuitive step-by-step room booking system with instant confirmation safeguards."
        />
        <meta
          name="keywords"
          content="reserve room Jodhpur, online hotel booking Rajasthan, holiday reservation Jodhpur, royal suite booking Jodhpur, secure resort reservation Jodhpur, booking form Chopasni"
        />
        <link rel="canonical" href="https://harikunjresort.com/booking" />
        <meta property="og:title" content="Online Room Booking & Reservations | HariKunj Resort Jodhpur" />
        <meta property="og:description" content="Book your royal rooms and suites online at HariKunj Resort, Jodhpur. Safe, secure, and intuitive step-by-step room booking system with instant confirmation safeguards." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://harikunjresort.com/booking" />
        <meta property="og:image" content="https://harikunjresort.com/assets/img/image-2.jpg" />
      </Helmet>
      
      <Heading heading="Royal Reservations" title="Home" subtitle="Reservations" />

      <main className="booking-page-container container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="luxury-booking-wrapper">
              
              {!isSubmitted ? (
                <>
                  {/* Stepper Header */}
                  <div className="page-stepper">
                    <div className={`page-step ${step >= 1 ? "page-step--active" : ""}`}>
                      <span className="step-num">1</span>
                      <span className="step-text">Guest Details</span>
                    </div>
                    <div className={`page-step ${step >= 2 ? "page-step--active" : ""}`}>
                      <span className="step-num">2</span>
                      <span className="step-text">Stay & Guests</span>
                    </div>
                    <div className={`page-step ${step >= 3 ? "page-step--active" : ""}`}>
                      <span className="step-num">3</span>
                      <span className="step-text">Review Booking</span>
                    </div>
                  </div>

                  {submitError && <div className="alert alert-danger mb-3" role="alert">{submitError}</div>}

                  <form onSubmit={handleSubmit} className="booking-form">
                    <div className="booking-form-body">
                      
                      {/* STEP 1: Guest details */}
                      {step === 1 && (
                        <div className="step-panel fade-in">
                          <h3 className="panel-title">Enter Guest Details</h3>
                          <div className="form-group mb-3">
                            <label htmlFor="fullName">Full Name</label>
                            <div className="unique-input-icon-wrapper">
                              <input
                                type="text"
                                id="fullName"
                                name="fullName"
                                className={`unique-dojo-input ${errors.fullName ? "is-invalid" : ""}`}
                                placeholder="e.g. Maharana Pratap"
                                value={formData.fullName}
                                onChange={handleChange}
                              />
                              <i className="fa fa-user unique-field-icon"></i>
                            </div>
                            {errors.fullName && <span className="error-text">{errors.fullName}</span>}
                          </div>

                          <div className="form-group mb-3">
                            <label htmlFor="email">Email Address</label>
                            <div className="unique-input-icon-wrapper">
                              <input
                                type="email"
                                id="email"
                                name="email"
                                className={`unique-dojo-input ${errors.email ? "is-invalid" : ""}`}
                                placeholder="name@royalindia.com"
                                value={formData.email}
                                onChange={handleChange}
                              />
                              <i className="fa fa-envelope unique-field-icon"></i>
                            </div>
                            {errors.email && <span className="error-text">{errors.email}</span>}
                          </div>

                          <div className="form-group mb-3">
                            <label htmlFor="number">Phone Number</label>
                            <div className="unique-input-icon-wrapper">
                              <input
                                type="tel"
                                id="number"
                                name="number"
                                className={`unique-dojo-input ${errors.number ? "is-invalid" : ""}`}
                                placeholder="+91 9876543210"
                                value={formData.number}
                                onChange={handleChange}
                              />
                              <i className="fa fa-phone unique-field-icon"></i>
                            </div>
                            {errors.number && <span className="error-text">{errors.number}</span>}
                          </div>
                        </div>
                      )}

                      {/* STEP 2: Stay details */}
                      {step === 2 && (
                        <div className="step-panel fade-in">
                          <h3 className="panel-title">Your Schedule & Room</h3>
                          <div className="row">
                            <div className="col-md-6 form-group mb-3">
                              <label htmlFor="checkInDate">Check-In Date</label>
                              <div className="unique-input-icon-wrapper">
                                <input
                                  type="date"
                                  id="checkInDate"
                                  name="checkInDate"
                                  className={`unique-dojo-input ${errors.checkInDate ? "is-invalid" : ""}`}
                                  value={formData.checkInDate}
                                  onChange={handleChange}
                                  min={getTodayString()}
                                />
                                <i className="fa fa-calendar-alt unique-field-icon"></i>
                              </div>
                              {errors.checkInDate && <span className="error-text">{errors.checkInDate}</span>}
                            </div>

                            <div className="col-md-6 form-group mb-3">
                              <label htmlFor="checkInTime">Check-In Time</label>
                              <div className="unique-input-icon-wrapper">
                                <input
                                  type="time"
                                  id="checkInTime"
                                  name="checkInTime"
                                  className={`unique-dojo-input ${errors.checkInTime ? "is-invalid" : ""}`}
                                  value={formData.checkInTime}
                                  onChange={handleChange}
                                />
                                <i className="fa fa-clock unique-field-icon"></i>
                              </div>
                              {errors.checkInTime && <span className="error-text">{errors.checkInTime}</span>}
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-md-6 form-group mb-3">
                              <label htmlFor="checkOutDate">Check-Out Date</label>
                              <div className="unique-input-icon-wrapper">
                                <input
                                  type="date"
                                  id="checkOutDate"
                                  name="checkOutDate"
                                  className={`unique-dojo-input ${errors.checkOutDate ? "is-invalid" : ""}`}
                                  value={formData.checkOutDate}
                                  onChange={handleChange}
                                  min={formData.checkInDate || getTodayString()}
                                />
                                <i className="fa fa-calendar-alt unique-field-icon"></i>
                              </div>
                              {errors.checkOutDate && <span className="error-text">{errors.checkOutDate}</span>}
                            </div>

                            <div className="col-md-6 form-group mb-3">
                              <label htmlFor="checkOutTime">Check-Out Time</label>
                              <div className="unique-input-icon-wrapper">
                                <input
                                  type="time"
                                  id="checkOutTime"
                                  name="checkOutTime"
                                  className={`unique-dojo-input ${errors.checkOutTime ? "is-invalid" : ""}`}
                                  value={formData.checkOutTime}
                                  onChange={handleChange}
                                />
                                <i className="fa fa-clock unique-field-icon"></i>
                              </div>
                              {errors.checkOutTime && <span className="error-text">{errors.checkOutTime}</span>}
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-md-6 form-group mb-3">
                              <label htmlFor="adults">Adults</label>
                              <div className="unique-input-icon-wrapper">
                                <select
                                  id="adults"
                                  name="adults"
                                  className="unique-dojo-select"
                                  value={formData.adults}
                                  onChange={handleChange}
                                >
                                  <option value="adult 1">1 Adult</option>
                                  <option value="adult 2">2 Adults</option>
                                  <option value="adult 3">3 Adults</option>
                                  <option value="adult 4">4+ Adults</option>
                                </select>
                                <i className="fa fa-users unique-field-icon"></i>
                              </div>
                            </div>

                            <div className="col-md-6 form-group mb-3">
                              <label htmlFor="children">Children</label>
                              <div className="unique-input-icon-wrapper">
                                <select
                                  id="children"
                                  name="children"
                                  className="unique-dojo-select"
                                  value={formData.children}
                                  onChange={handleChange}
                                >
                                  <option value="Child 0">No Children</option>
                                  <option value="Child 1">1 Child</option>
                                  <option value="Child 2">2 Children</option>
                                  <option value="Child 3">3+ Children</option>
                                </select>
                                <i className="fa fa-child unique-field-icon"></i>
                              </div>
                            </div>
                          </div>

                          <div className="form-group mb-3">
                            <label htmlFor="roomType">Heritage Suite / Room Category</label>
                            <div className="unique-input-icon-wrapper">
                              <select
                                id="roomType"
                                name="roomType"
                                className="unique-dojo-select"
                                value={formData.roomType}
                                onChange={handleChange}
                              >
                                <option value="Standard Room">Standard Room (Comfort Stay)</option>
                                <option value="Deluxe Room">Deluxe Heritage Room</option>
                                <option value="Super Deluxe">Super Deluxe Royal Suite</option>
                              </select>
                              <i className="fa fa-bed unique-field-icon"></i>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* STEP 3: Review */}
                      {step === 3 && (
                        <div className="step-panel fade-in">
                          <h3 className="panel-title">Review Your Luxury Stay</h3>
                          <div className="summary-card">
                            <div className="summary-row">
                              <span>Guest Name:</span>
                              <strong>{formData.fullName}</strong>
                            </div>
                            <div className="summary-row">
                              <span>Contact Email:</span>
                              <strong>{formData.email}</strong>
                            </div>
                            <div className="summary-row">
                              <span>Phone Number:</span>
                              <strong>{formData.number}</strong>
                            </div>
                            <div className="summary-row">
                              <span>Check-In Stay:</span>
                              <strong>{formData.checkInDate} ({formData.checkInTime})</strong>
                            </div>
                            <div className="summary-row">
                              <span>Check-Out Stay:</span>
                              <strong>{formData.checkOutDate} ({formData.checkOutTime})</strong>
                            </div>
                            <div className="summary-row">
                              <span>Guests:</span>
                              <strong>{formData.adults}, {formData.children}</strong>
                            </div>
                            <div className="summary-row highlighted">
                              <span>Selected Category:</span>
                              <strong>{formData.roomType}</strong>
                            </div>
                            <div className="summary-row" style={{ borderTop: "1px dashed rgba(255, 255, 255, 0.1)", paddingTop: "8px", marginTop: "8px" }}>
                              <span>Price per Night:</span>
                              <strong>₹{getRoomPrice().toLocaleString("en-IN")}</strong>
                            </div>
                            <div className="summary-row">
                              <span>Total Duration:</span>
                              <strong>{calculateNights()} {calculateNights() === 1 ? "Night" : "Nights"}</strong>
                            </div>
                            <div className="summary-row">
                              <span>Room Subtotal:</span>
                              <strong>₹{(getRoomPrice() * calculateNights()).toLocaleString("en-IN")}</strong>
                            </div>
                            {appliedCoupon && (
                              <div className="summary-row text-success">
                                <span>Discount Applied ({appliedCoupon.code}):</span>
                                <strong>-₹{appliedCoupon.discountAmount.toLocaleString("en-IN")}</strong>
                              </div>
                            )}
                            <div className="summary-row highlighted" style={{ borderTop: "2px solid #FEA116", paddingTop: "8px", marginTop: "8px", color: "#FEA116" }}>
                              <span>Estimated Total:</span>
                              <strong>
                                ₹{(appliedCoupon ? appliedCoupon.finalCost : (getRoomPrice() * calculateNights())).toLocaleString("en-IN")}
                              </strong>
                            </div>
                          </div>

                          {/* Promo Coupon Form Field */}
                          <div className="coupon-promo-section mt-4 p-3 rounded" style={{ background: "rgba(255, 255, 255, 0.03)", border: "1px solid rgba(255, 255, 255, 0.08)" }}>
                            <label className="form-label text-light small fw-bold mb-2">HAVE A PROMO / COUPON CODE?</label>
                            <div className="d-flex gap-2">
                              <input
                                type="text"
                                className="form-control unique-dojo-input m-0 text-uppercase"
                                style={{ flex: 1, backgroundColor: "#1e293b", color: "#f8fafc", border: "1px solid #475569" }}
                                placeholder="Enter Code (e.g. WELCOME10)"
                                value={couponCodeInput}
                                onChange={(e) => {
                                  setCouponCodeInput(e.target.value.toUpperCase());
                                  setCouponError("");
                                }}
                                disabled={appliedCoupon}
                              />
                              {appliedCoupon ? (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger px-3"
                                  onClick={() => {
                                    setAppliedCoupon(null);
                                    setCouponCodeInput("");
                                  }}
                                >
                                  Remove
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-warning px-4"
                                  style={{ whiteSpace: "nowrap", backgroundColor: "#FEA116", color: "#0F172B", fontWeight: "600", border: "none" }}
                                  onClick={handleApplyCoupon}
                                  disabled={isValidatingCoupon || !couponCodeInput}
                                >
                                  {isValidatingCoupon ? "Checking..." : "Apply"}
                                </button>
                              )}
                            </div>
                            {couponError && <div className="text-danger small mt-1">{couponError}</div>}
                            {appliedCoupon && (
                              <div className="text-success small mt-1">
                                <i className="fa fa-check-circle me-1" /> Coupon <strong>{appliedCoupon.code}</strong> applied! You saved ₹{appliedCoupon.discountAmount}.
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                    </div>

                    {/* Stepper Footer Controls */}
                    <div className="booking-form-footer">
                      {step > 1 && (
                        <button
                          type="button"
                          className="btn btn-back"
                          onClick={handleBack}
                        >
                          Back
                        </button>
                      )}
                      
                      {step < 3 ? (
                        <button
                          type="button"
                          className="btn btn-next"
                          onClick={handleNext}
                        >
                          Next Step
                        </button>
                      ) : (
                        <button
                          type="submit"
                          className="btn btn-submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Processing Inquiry..." : "Confirm Heritage Booking"}
                        </button>
                      )}
                    </div>
                  </form>
                </>
              ) : (
                <div className="booking-success-message text-center">
                  <div className="success-badge">✓</div>
                  <h2>Enquiry Received!</h2>
                  <p className="success-tagline">Welcome to the family of royal guests.</p>
                  <p className="success-sub">
                    Our guest relations desk in Jodhpur has been notified. We will call you within 24 hours to secure your details.
                  </p>
                  
                  <div className="success-summary">
                    <h4>Reservation Details</h4>
                    <p><strong>Lead Guest:</strong> {formData.fullName}</p>
                    <p><strong>Suite Type:</strong> {formData.roomType}</p>
                    <p><strong>Duration:</strong> {formData.checkInDate} to {formData.checkOutDate}</p>
                  </div>

                  <div className="success-actions">
                    <button
                      onClick={() => {
                        const whatsappMsg = `Hello HariKunj Resort! I want to confirm my booking details:
Name: ${formData.fullName}
Email: ${formData.email}
Phone: ${formData.number}
Check-in: ${formData.checkInDate} ${formData.checkInTime}
Check-out: ${formData.checkOutDate} ${formData.checkOutTime}
Room: ${formData.roomType}
Guests: ${formData.adults}, ${formData.children}`;

                        const whatsappUrl = `https://wa.me/919602588882?text=${encodeURIComponent(whatsappMsg)}`;
                        window.open(whatsappUrl, "_blank");
                      }}
                      className="btn btn-whatsapp"
                    >
                      Instant WhatsApp Confirmation
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </main>

      <style>{`
        .luxury-booking-wrapper {
          background: linear-gradient(135deg, rgba(11, 29, 55, 0.96), rgba(5, 14, 26, 0.98));
          padding: 40px;
          border-radius: 24px;
          box-shadow: 0 22px 60px rgba(5, 14, 26, 0.4);
          border: 1.5px solid rgba(212, 175, 55, 0.55);
          color: #FAF6F0;
          font-family: 'Poppins', sans-serif;
          margin-bottom: 50px;
        }

        /* Stepper styles */
        .page-stepper {
          display: flex;
          justify-content: space-between;
          margin-bottom: 40px;
          border-bottom: 1.5px solid rgba(212, 175, 55, 0.2);
          padding-bottom: 20px;
        }

        .page-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          flex: 1;
          opacity: 0.35;
          transition: all 0.3s ease;
        }

        .page-step--active {
          opacity: 1;
        }

        .step-num {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 1px solid rgba(212, 175, 55, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          background: #0B1D37;
          color: #FAF6F0;
          margin-bottom: 8px;
        }

        .page-step--active .step-num {
          background: linear-gradient(135deg, #FEA116, #D4AF37);
          color: #050E1A;
          border-color: #ffffff;
          box-shadow: 0 0 14px rgba(254, 161, 22, 0.4);
        }

        .step-text {
          font-size: 0.78rem;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          font-weight: 600;
          text-align: center;
        }

        .panel-title {
          font-family: 'Cinzel', serif;
          font-size: 1.4rem;
          color: #FEA116;
          margin-bottom: 25px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-left: 3px solid #FEA116;
          padding-left: 10px;
        }

        .form-group label {
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          font-weight: 600;
          margin-bottom: 8px;
          color: #FAF6F0;
        }

        .form-control, .form-select {
          background: rgba(30, 62, 114, 0.18) !important;
          border: 1.5px solid rgba(212, 175, 55, 0.35) !important;
          color: #FAF6F0 !important;
          border-radius: 12px;
          padding: 12px 18px;
          font-size: 0.95rem;
          transition: all 0.3s ease;
        }

        .form-control:focus, .form-select:focus {
          outline: none;
          border-color: #FEA116 !important;
          box-shadow: 0 0 12px rgba(254, 161, 22, 0.25) !important;
          background: rgba(30, 62, 114, 0.28) !important;
        }

        .error-text {
          color: #ff6b6b;
          font-size: 0.78rem;
          font-weight: 500;
          margin-top: 4px;
          display: block;
        }

        .summary-card {
          background: rgba(30, 62, 114, 0.2);
          border-radius: 16px;
          border: 1px solid rgba(212, 175, 55, 0.35);
          padding: 24px;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          padding: 10px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
          font-size: 0.95rem;
        }

        .summary-row:last-child {
          border-bottom: none;
        }

        .summary-row.highlighted {
          border-top: 1.5px dashed rgba(212, 175, 55, 0.4);
          padding-top: 14px;
          margin-top: 10px;
          font-size: 1.05rem;
        }

        .summary-row.highlighted strong {
          color: #FEA116;
        }

        .booking-form-footer {
          margin-top: 35px;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          border-top: 1px solid rgba(212, 175, 55, 0.15);
          padding-top: 20px;
        }

        .btn-next, .btn-submit {
          background: linear-gradient(135deg, #FEA116, #D4AF37) !important;
          color: #050E1A !important;
          font-weight: 700;
          padding: 12px 28px;
          border-radius: 30px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          border: none;
          box-shadow: 0 8px 24px rgba(254, 161, 22, 0.35);
          transition: all 0.3s ease;
        }

        .btn-next:hover, .btn-submit:hover {
          background: linear-gradient(135deg, #ff8c00, #fea116) !important;
          transform: translateY(-2px);
          box-shadow: 0 12px 28px rgba(254, 161, 22, 0.5);
        }

        .btn-back {
          background: rgba(30, 62, 114, 0.3);
          color: #FAF6F0;
          border: 1px solid rgba(255, 255, 255, 0.15);
          font-weight: 600;
          padding: 12px 24px;
          border-radius: 30px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          transition: all 0.3s ease;
        }

        .btn-back:hover {
          background: rgba(30, 62, 114, 0.55);
          color: #ffffff;
        }

        /* Success screen styles */
        .booking-success-message {
          padding: 40px 10px;
        }

        .success-badge {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: linear-gradient(135deg, #FEA116, #D4AF37);
          color: #050E1A;
          font-size: 2.2rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 24px;
          font-weight: bold;
          box-shadow: 0 10px 28px rgba(254, 161, 22, 0.4);
        }

        .booking-success-message h2 {
          font-family: 'Cinzel', serif;
          font-size: 2rem;
          color: #FEA116;
          margin-bottom: 8px;
          font-weight: 700;
        }

        .success-tagline {
          font-size: 1.1rem;
          color: #FAF6F0;
          font-style: italic;
          margin-bottom: 20px;
        }

        .success-sub {
          color: rgba(250, 246, 240, 0.85);
          max-width: 550px;
          margin: 0 auto 30px;
          line-height: 1.6;
        }

        .success-summary {
          max-width: 500px;
          margin: 0 auto 30px;
          background: rgba(30, 62, 114, 0.2);
          border-radius: 16px;
          border: 1px solid rgba(212, 175, 55, 0.35);
          padding: 20px 30px;
          text-align: left;
        }

        .success-summary h4 {
          font-family: 'Cinzel', serif;
          font-size: 1.2rem;
          color: #FEA116;
          border-bottom: 1px solid rgba(255,255,255,0.1);
          padding-bottom: 8px;
          margin-bottom: 12px;
        }

        .success-summary p {
          margin-bottom: 8px;
          display: flex;
          justify-content: space-between;
        }

        .btn-whatsapp {
          background: linear-gradient(135deg, #25D366, #128C7E) !important;
          color: #ffffff !important;
          font-weight: 700;
          padding: 15px 35px;
          border-radius: 35px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          border: none;
          box-shadow: 0 8px 24px rgba(37, 211, 102, 0.3);
          transition: all 0.3s ease;
        }

        .btn-whatsapp:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 28px rgba(37, 211, 102, 0.5);
        }

        @media (max-width: 768px) {
          .luxury-booking-wrapper {
            padding: 24px;
          }
          .page-stepper {
            flex-wrap: wrap;
            gap: 15px;
          }
          .success-summary {
            padding: 15px;
          }
        }
      `}</style>
    </>
  );
}
